import {BasePlug, LangId} from '@cip/code-runner';
import {getFunctionsBaseUrl} from '@cip/core/firebase';
import {getAuth} from 'firebase/auth';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import {firebaseConfig} from 'config';
import {getFirebaseEmulatorRuntimeState} from 'firebaseApp';
import {ensureSyncMessageServiceWorkerReady} from 'utils/syncMessageServiceWorker';
import {handleServiceWorkerError} from 'utils/handleServiceWorkerError';
import {reportRunHang} from 'components/errors/runHangReporter';
import {IS_PRODUCTION} from 'utils/general';

const SYNC_MESSAGE_SW_ERROR =
  'Sync service worker unavailable. Please reload the page and try again.';

interface CodeContextType {
  pythonClient: BasePlug;
  pythonClientLoaded: boolean;
  pythonClientError: Error | null;
  /**
   * True when `pythonClientError` only means the load ran out of time: the
   * runtime may still finish loading on its own, and a retry starts the
   * download over.
   */
  isPythonClientLoadTimedOut: boolean;
  /**
   * Load attempts that have failed in a row since the last successful load,
   * counting each attempt once. Lets a consumer stop suggesting a retry when
   * the cause is persistent.
   */
  failedPythonClientLoadAttempts: number;
  /**
   * True while the shared Pyodide client has any run queued or in flight
   * — including the brief window between `interrupt()` and the next run
   * being safe to dispatch. Backed by `BasePlug.isBusy()` /
   * `onRunStatusChange()`. Consumers use this to gate Run-intent clicks
   * so they don't race with an in-progress teardown after an assignment
   * switch. See https://github.com/CodeinPlace/cip/issues/2869.
   */
  isPyodideBusy: boolean;
  /**
   * Starts loading the Python runtime if it has not started already.
   * Idempotent. Prefer {@link usePythonRuntime} over calling this by hand.
   */
  requestPythonClient: () => void;
  /**
   * Clears `pythonClientError` and loads the Python runtime again. For a
   * "Try again" control after a failed load; a no-op when there is no error.
   */
  retryPythonClient: () => void;
}

const defaultData = {
  pythonClient: null,
  pythonClientLoaded: false,
  pythonClientError: null,
  isPythonClientLoadTimedOut: false,
  failedPythonClientLoadAttempts: 0,
  isPyodideBusy: false,
  requestPythonClient: () => {},
  retryPythonClient: () => {},
};

export const CodeContext = createContext<CodeContextType>(defaultData);

/**
 * Reads {@link CodeContext} and asks for the Python runtime to be loaded.
 *
 * Use this instead of `useContext(CodeContext)` anywhere that can actually run
 * Python, so the surface still works under a deferred provider (see
 * {@link CodeProviderProps.deferUntilRequested}).
 *
 * @return The same value `useContext(CodeContext)` returns.
 */
export function usePythonRuntime(): CodeContextType {
  const context = useContext(CodeContext);
  const {requestPythonClient} = context;
  useEffect(() => {
    requestPythonClient();
  }, [requestPythonClient]);
  return context;
}

/** Props for {@link CodeProvider}. */
interface CodeProviderProps {
  /** Subtree that may consume {@link CodeContext}. */
  children: React.ReactNode;
  /**
   * Wait for {@link CodeContextType.requestPythonClient} instead of loading the
   * multi-megabyte Python runtime as soon as the provider mounts. Omitted or
   * false, the runtime loads on mount.
   *
   * Flipping this to false later starts the load, so a provider that stays
   * mounted across a route change still ends up eager on the new route.
   */
  deferUntilRequested?: boolean;
}

export const CodeProvider = ({
  children,
  deferUntilRequested = false,
}: CodeProviderProps) => {
  const [pyodideLoaded, setPyodideLoaded] = useState<boolean>(false);
  const [librariesLoaded, setLibrariesLoaded] = useState<boolean>(false);
  const [pythonClientLoaded, setPythonClientLoaded] = useState<boolean>(false);
  const [pythonClientError, setPythonClientError] = useState<Error | null>(
    null,
  );
  const [isPythonClientLoadTimedOut, setIsPythonClientLoadTimedOut] =
    useState(false);
  const pythonClientRef = useRef<BasePlug>(null);
  const graphicsWhl = new URL(
    '/pyodide/graphics-0.0.4-py3-none-any.whl',
    window.location.origin,
  ).href;
  const karelWhl = new URL(
    '/pyodide/karel-0.0.1-py3-none-any.whl',
    window.location.origin,
  ).href;
  const aiWhl = new URL(
    '/pyodide/ai-0.0.1-py3-none-any.whl',
    window.location.origin,
  ).href;

  const isMountedRef = useRef(true);
  const loadStartedRef = useRef(false);
  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    [],
  );

  const [failedPythonClientLoadAttempts, setFailedPythonClientLoadAttempts] =
    useState(0);
  // One attempt can report failure twice (a timeout, then the worker's own
  // late error), so count the attempt, not the report.
  const hasCurrentAttemptFailedRef = useRef(false);
  const recordLoadFailure = useCallback(() => {
    if (hasCurrentAttemptFailedRef.current) {
      return;
    }
    hasCurrentAttemptFailedRef.current = true;
    setFailedPythonClientLoadAttempts(count => count + 1);
  }, []);

  const requestPythonClient = useCallback(() => {
    if (loadStartedRef.current) {
      return;
    }
    loadStartedRef.current = true;
    void (async () => {
      try {
        await ensureSyncMessageServiceWorkerReady();
      } catch (error) {
        if (isMountedRef.current) {
          // Prompt here rather than at boot: this is the first point where we
          // know the code runner was wanted, so it is the first point where
          // the failure is worth interrupting anyone over. `pythonClientError`
          // alone would not tell them — consumers only read its truthiness
          // (the IDE's Run button just reads "Error"), never its message.
          void handleServiceWorkerError({
            error,
            serviceWorkerSupported: 'serviceWorker' in navigator,
          });
          recordLoadFailure();
          if (error instanceof Error) {
            setPythonClientError(
              new Error(`${SYNC_MESSAGE_SW_ERROR} (${error.message})`),
            );
          } else {
            setPythonClientError(new Error(SYNC_MESSAGE_SW_ERROR));
          }
        }
        return;
      }

      // Decide which Cloud Functions base URL Pyodide libraries
      // (e.g. `call_gpt`) should target. When the webapp's
      // Functions emulator hookup is active, point Pyodide at
      // the emulator so the same wheel works against both prod
      // and local-dev / browser-test mode without env-aware
      // logic in Python. Otherwise default to the production
      // URL. `getFunctionsBaseUrl` is the SSOT for region + URL
      // shape; project ID comes from the webapp's own config.
      const emulatorState = getFirebaseEmulatorRuntimeState();
      const functionsBaseUrl = getFunctionsBaseUrl({
        projectId: firebaseConfig.projectId,
        emulator: emulatorState.functions.connected
          ? {
              host: emulatorState.functions.host,
              port: emulatorState.functions.port,
            }
          : null,
      });

      pythonClientRef.current = new BasePlug(LangId.PYTHON, {
        onLoadCallback: data => {
          if (!isMountedRef.current) {
            return;
          }
          if (data.success) {
            // A worker that timed out can still report ready afterwards.
            setPythonClientError(null);
            setIsPythonClientLoadTimedOut(false);
            hasCurrentAttemptFailedRef.current = false;
            setFailedPythonClientLoadAttempts(0);
            setPyodideLoaded(true);
          } else {
            recordLoadFailure();
            setIsPythonClientLoadTimedOut(Boolean(data.isTimeout));
            const error = new Error(
              data.error || 'Unknown error loading python code runner',
            );
            // The worker's own console is not forwarded by production logging.
            console.error('[CodeContext] Python runner failed to load:', error.message);
            setPythonClientError(error);
          }
        },
        // Forward a fresh Firebase ID token on runs that set
        // `attachAuthToken` so Pyodide libraries that POST to Cloud
        // Functions (e.g. `call_gpt`) can authenticate the caller. Every
        // other run, including all of someone else's code, gets none.
        // `getIdToken()` auto-refreshes
        // when near expiry, so the worker never holds a stale token.
        // Returns null for signed-out viewers (anonymous share-page
        // visitors), and the server falls back to its unauthenticated
        // path.
        getAuthToken: async () => {
          const user = getAuth().currentUser;
          if (!user) {
            return null;
          }
          return user.getIdToken();
        },
        functionsBaseUrl,
        // [#2879](https://github.com/CodeinPlace/cip/issues/2879): production only so dev doesn't churn the collection.
        onRunHang: IS_PRODUCTION ? reportRunHang : undefined,
      });

      pythonClientRef.current.registerApi({
        id: 'graphics',
        srcPath: graphicsWhl,
        callback: () => {},
      });

      pythonClientRef.current.registerApi({
        id: 'karel',
        srcPath: karelWhl,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
        callback: (func, args) => {},
        // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
        onStep: serialization => {},
      });
      pythonClientRef.current.registerApi({
        id: 'ai',
        srcPath: aiWhl,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
        callback: (func, args) => {},
        // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
        onStep: serialization => {},
      });
      if (isMountedRef.current) {
        setLibrariesLoaded(true);
      }
    })();
    // The wheel URLs derive from `window.location.origin`, so they are fixed for
    // the page's lifetime and are deliberately not dependencies.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  }, []);

  const retryPythonClient = useCallback(() => {
    if (!pythonClientError) {
      return;
    }
    setPythonClientError(null);
    setIsPythonClientLoadTimedOut(false);
    hasCurrentAttemptFailedRef.current = false;
    // A failure can follow a successful load (a respawn after Stop), so the
    // loaded flags must drop or consumers offer Run during the reload.
    setPyodideLoaded(false);
    setPythonClientLoaded(false);
    const client = pythonClientRef.current;
    if (client) {
      client.restart();
      return;
    }
    // The sync-message service worker failed before a client was created.
    loadStartedRef.current = false;
    requestPythonClient();
  }, [pythonClientError, requestPythonClient]);

  // Depends on `deferUntilRequested` rather than running once on mount, so a
  // provider that stays mounted while the route changes still starts the load
  // when the flag flips.
  useEffect(() => {
    if (deferUntilRequested) {
      return;
    }
    requestPythonClient();
  }, [deferUntilRequested, requestPythonClient]);

  useEffect(() => {
    if (pyodideLoaded && librariesLoaded) {
      // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
      setPythonClientLoaded(true);
    }
  }, [pyodideLoaded, librariesLoaded]);

  // Mirror `BasePlug`'s busy state into React via the React 18
  // external-store hook. The plug emits `true` on the first `runCode`
  // and `false` after the last record's finally block removes it from
  // `runs[]`, so this stays `true` across the interrupt-then-teardown
  // window after an assignment switch (the core race that motivates
  // gating Run-intent clicks on this flag). Falls back to `false`
  // until `pythonClientLoaded`, before which there is no plug to
  // query and no run could be in flight.
  const subscribeIsBusy = useCallback(
    (onChange: () => void) => {
      if (!pythonClientLoaded) return () => {};
      const client = pythonClientRef.current;
      if (!client) return () => {};
      return client.onRunStatusChange(onChange);
    },
    [pythonClientLoaded],
  );
  const getIsBusySnapshot = useCallback(() => {
    if (!pythonClientLoaded) return false;
    return pythonClientRef.current?.isBusy() ?? false;
  }, [pythonClientLoaded]);
  const isPyodideBusy = useSyncExternalStore(
    subscribeIsBusy,
    getIsBusySnapshot,
  );

  return (
    <CodeContext.Provider
      value={{
        pythonClient: pythonClientRef.current,
        pythonClientLoaded,
        pythonClientError,
        isPythonClientLoadTimedOut,
        failedPythonClientLoadAttempts,
        isPyodideBusy,
        requestPythonClient,
        retryPythonClient,
      }}
    >
      {children}
    </CodeContext.Provider>
  );
};
